import { NO_CONSUMABLES } from "./Consumable";
import { GameState } from "./GameState";
import { STARTING_ARTICLES } from "./manual";
import { STARTER_SKILLS } from "./Skill";
import { ToolId } from "./Tool";
import {
  defaultParametersFor,
  MACHINE_TYPES,
  MachineId,
  MachineState,
  ParameterValues,
} from "./Machine";
import { Direction } from "./Vectors";
import { defaultEntrancePosition } from "./ShopInfo";

export const initialGameState: GameState = {
  minute: 0,
  day: 1,
  dayStartMinute: 0,
  money: 0,
  reputation: 0,
  // An empty floor: the first pallet is scavenged with the truck
  materialPiles: [],
  // No starter kit: supplies come from the store, or back out of salvage
  // (prying a pallet apart recovers its nails)
  consumables: NO_CONSUMABLES,
  // Clamps are bought the same way. Nothing you can build on day one needs
  // them — the first glue-up is what sends you to the store for a pair.
  clamps: 0,
  player: {
    name: "Player",
    position: [6, 12],
    direction: 1,
    inventory: [],
    away: null,
  },
  machines: [
    // Single workspace for tutorial, with the starter hammer mounted
    // (3×2 ft, against the back wall with its front apron open)
    machine("workspace", [2, 2], 0, ["hammer"]),
    // Garbage can for disposing unwanted materials (2×2 ft, in a corner)
    machine("garbageCan", [0, 13], 0),
    // A small lumber shelf against the back wall, so the first scavenged
    // boards have an obvious home besides the floor (2×1 ft)
    machine("lumberShelf", [8, 0], 0),
  ],
  machineCrates: [],
  truck: { bed: [], crates: [] },
  storage: {
    upgrades: [],
  },
  shopInfo: {
    name: "One Car Garage",
    electricity: 120,
    // 12' × 16', in 1-ft cells
    size: [12, 16],
    materialDropoffPosition: [10, 13],
    entrancePosition: defaultEntrancePosition([12, 16]),
  },
  progression: {
    tutorials: {
      opening: { step: 0, dismissed: false },
      firstNight: { step: 0, dismissed: false },
      dust: { step: 0, dismissed: false },
    },
    // The story hasn't started: the opener plays on the first tick
    story: { scenesPlayed: [] },
    storeUnlocked: false,
    standUnlocked: false,
    lumberyardUnlocked: false,
    salesCompleted: 0,
    sweepingUnlocked: false,
    // Welcome starts unread on purpose: it keeps the ? badge lit until the
    // player opens the manual for the first time.
    unlockedArticles: STARTING_ARTICLES,
    readArticles: [],
    xp: 0,
    skillPoints: 0,
    unlockedSkills: STARTER_SKILLS,
  },
  stand: [],
  // Grass until the story offers the spot and the table is set up
  standSetUp: false,
  customers: [],
  dust: {},
  shopVac: null,
  broomOwned: false,
  broomPosition: null,
  dustpan: {},
  pendingSounds: [],
  pendingPayouts: [],
};

// Helper method to create a machine with less boilerplate
function machine(
  machineTypeId: MachineId,
  position: [number, number],
  rotation: Direction,
  tools: ReadonlyArray<ToolId> = [],
): MachineState {
  const machineType = MACHINE_TYPES[machineTypeId];
  const firstOperation = machineType.operations[0];
  const selectedParameters: ParameterValues | undefined = firstOperation
    ? defaultParametersFor(firstOperation)
    : undefined;

  return {
    machineTypeId,
    position,
    rotation,
    inputMaterials: [],
    processingMaterials: [],
    outputMaterials: [],
    tools,
    storedMaterials: [],
    // Stations with no operations (the lumber shelf) park on "none",
    // matching freshMachineState in machine-actions.ts
    selectedOperationId: firstOperation ? firstOperation.id : "none",
    selectedParameters,
    operationProgress: {
      status: "notStarted",
      phaseIndex: 0,
      minutesRemaining: 0,
    },
  };
}
